import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { LocalStorageClass } from '../../../helpers/local-storage-helper';
import { JenzButton } from '../jenz-button/jenz-button';

interface Language {
  label: string;
  value: string;
  termsAndConditionsUrl: string;
}

@Component({
  selector: 'jenz-first-time-setup-dialog',
  imports: [DialogModule, CheckboxModule, FormsModule, JenzButton, TranslateModule],
  templateUrl: './first-time-setup-dialog.html',
  styleUrl: './first-time-setup-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirstTimeSetupDialog {
  private readonly translate = inject(TranslateService);

  @Output() setupCompleted = new EventEmitter<void>();

  selectedLanguage = signal<Language | null>(null);
  termsAccepted = signal(false);

  canProceed = computed(() => !!this.selectedLanguage() && this.termsAccepted());

  privacyPolicyUrl = computed(
    () => this.selectedLanguage()?.termsAndConditionsUrl ?? 'https://jenz.app/privacy-policy/?preview=true',
  );

  readonly languages: Language[] = [
    { label: 'English', value: 'en', termsAndConditionsUrl: 'https://jenz.app/privacy-policy/?preview=true' },
    { label: 'Deutsch', value: 'de', termsAndConditionsUrl: 'https://jenz.app/privacy-policy-de/?preview=true' },
    { label: 'Hrvatski', value: 'hr', termsAndConditionsUrl: 'https://jenz.app/privacy-policy-hr/?preview=true' },
    { label: 'Italiano', value: 'it', termsAndConditionsUrl: 'https://jenz.app/privacy-policy-it/?preview=true' },
    { label: 'Português', value: 'pt', termsAndConditionsUrl: 'https://jenz.app/privacy-policy-pt/?preview=true' },
    { label: 'Slovenščina', value: 'sl', termsAndConditionsUrl: 'https://jenz.app/privacy-policy-sl/?preview=true' },
    { label: 'Srpski', value: 'sr-Latn', termsAndConditionsUrl: 'https://jenz.app/privacy-policy/?preview=true' },
    { label: 'Bosanski', value: 'bs', termsAndConditionsUrl: 'https://jenz.app/privacy-policy/?preview=true' },
  ];

  selectLanguage(lang: Language): void {
    this.selectedLanguage.set(lang);
    this.translate.use(lang.value);
  }

  proceed(): void {
    if (!this.canProceed()) return;

    const lang = this.selectedLanguage()!;
    LocalStorageClass.setSelectedLanguage(lang.value);
    LocalStorageClass.acceptTermsAndConditions();
    this.setupCompleted.emit();
  }
}
